import { getCurrentInstance } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import type { ComputedRef } from "vue";
import {
	INVALID_CHANGES_DIALOG_TEXT,
	openDiscardChangesDialog,
	UNSAVED_CHANGES_DIALOG_OPTIONS,
	UNSAVED_CHANGES_DIALOG_TEXT,
	type ConfirmDialogApi,
	type DiscardDialogText
} from "@common-ui/utilities/unsavedChangesDialog";

/**
 * The dialog constants live in shared/frontend (LSP-19492) so shared components — notably
 * `media/ImageEditor.vue`, which contact-admin and contact-async both render — can reach the
 * same definitions this app's route guards use. They are re-exported here so every existing
 * `@/composables/useUnsavedChangesGuard` import keeps working; there is still only one
 * definition, in `@common-ui/utilities/unsavedChangesDialog`.
 */
export {
	UNSAVED_CHANGES_DIALOG_OPTIONS,
	UNSAVED_CHANGES_DIALOG_TEXT,
	INVALID_CHANGES_DIALOG_OPTIONS,
	INVALID_CHANGES_DIALOG_TEXT,
	openDiscardChangesDialog
} from "@common-ui/utilities/unsavedChangesDialog";
export type { ConfirmDialogApi, DiscardDialogText } from "@common-ui/utilities/unsavedChangesDialog";

type ConfirmRoot = {
	$root?: {
		$confirm?: ConfirmDialogApi;
	};
};

export async function confirmDiscardChanges(host: unknown, text: DiscardDialogText = INVALID_CHANGES_DIALOG_TEXT): Promise<boolean | null> {
	const confirmDialog = (host as ConfirmRoot | undefined)?.$root?.$confirm;
	if (!confirmDialog) return null;
	return openDiscardChangesDialog(confirmDialog, text);
}

/**
 * Pass `isValid` to switch to the two-way discard prompt (Cancel / Don't save)
 * when the form is dirty but can't be saved. Pass `save` returning false to
 * signal that the save callback handled navigation itself — the guard then
 * cancels the original route change to avoid competing navigations. Pass
 * `discardText` to override the discard prompt's copy when "some fields still
 * need fixing" would misdescribe why the changes can't be saved (e.g. an
 * embedded editor Contact can't save).
 */
export function useUnsavedChangesGuard(
	isDirty: ComputedRef<boolean>,
	save: () => Promise<void | false>,
	isValid?: ComputedRef<boolean> | (() => Promise<boolean>),
	discardText?: ComputedRef<DiscardDialogText>
): void {
	const instance = getCurrentInstance();

	onBeforeRouteLeave(async () => {
		if (!isDirty.value) return;

		let formIsValid = true;
		if (isValid) {
			try {
				formIsValid = typeof isValid === "function" ? await isValid() : isValid.value;
			} catch (error) {
				console.warn("Unsaved-changes guard: form validation failed — blocking navigation", error);
				return false;
			}
		}

		if (!formIsValid) {
			let discard: boolean | null = false;
			try {
				discard = await confirmDiscardChanges(instance?.proxy, discardText?.value);
			} catch {
				// dialog promise rejected — cancel nav so the user isn't stranded.
				return false;
			}
			return discard ? undefined : false;
		}

		const confirmDialog = (instance?.proxy as unknown as ConfirmRoot | undefined)?.$root?.$confirm;
		let result: boolean | null | undefined;
		try {
			result = await confirmDialog?.open(
				UNSAVED_CHANGES_DIALOG_TEXT.title,
				UNSAVED_CHANGES_DIALOG_TEXT.message,
				false,
				UNSAVED_CHANGES_DIALOG_TEXT.agreeText,
				UNSAVED_CHANGES_DIALOG_TEXT.cancelText,
				false,
				false,
				UNSAVED_CHANGES_DIALOG_OPTIONS
			);
		} catch {
			return false;
		}
		if (result === null) return false;
		if (result) {
			try {
				const cancelDefault = await save();
				if (cancelDefault === false) return false;
			} catch (error) {
				// LSP-16279: save threw (server error / validation rejection). The save()
				// callback already showed the user a toast. Cancel navigation so the user
				// stays on the page, sees the error, and can retry — silently allowing
				// navigation would discard their changes with no visible explanation.
				console.warn("LSP-16279: unsaved-changes guard: save failed — blocking navigation so user can see the error", error);
				return false;
			}
		}
	});
}
